import { QBox, QButton, QResourceLabel, QSpinner, QText, SearchDomain, SearchResource } from '@qualio/ui-components';
import { useQriBulkDetails } from '../../../../hooks';
import TextOr from '../../../TextOr/TextOr';
import {
  QLookupStandardValueObject,
  StandardValueObject,
  SubComponentProps,
  ViewModeDictionary,
} from '../../EventFormField.types';
import ReadonlyWrapper from '../ReadOnlyWrapper';
import * as DisplayStrings from '../../../../displayStrings';
import { formResolvedQriDisplayText } from './selectedItemText';
import { useReferenceDrawer } from '../../../../hooks/useReferenceDrawer';
import { fieldTypeIsQObjectType } from '../../../../utils/eventFormUtils';
import { QObjectFieldType } from '../../../../types/formFields/common';
import { ResourceLabelThatTriggersReferenceDrawer } from '../ResourceLabelToTriggerReferenceDrawer';

const domainMap = {
  event: 'quality-events',
  document: 'documents',
  user: 'users',
  supplier: 'suppliers',
  change_request: 'change-requests',
  registry: 'registry',
  design_controls: 'design-controls',
  training_plan: 'training',
  document_template: 'documents',
  event_template: 'quality-events',
  task: 'task',
} as const satisfies Record<QObjectFieldType, SearchDomain | SearchResource>;

const QObjectField = (props: SubComponentProps) => {
  const { fieldValues, inputType, mode, setIsEditModalOpen } = props;
  const valueObjects = fieldValues as StandardValueObject[];
  const parsedValueObjects = valueObjects.map((valueObject) => {
    return JSON.parse(valueObject.value) as QLookupStandardValueObject;
  });
  const qris = parsedValueObjects.map((value) => value.qri);
  const { data: qrisBulkDetails, isLoading: isQrisDetailsLoading } = useQriBulkDetails(qris);

  const referenceDrawerProps = useReferenceDrawer();

  if (!fieldTypeIsQObjectType(inputType)) {
    console.log('QObjectField was called with an unsupported field type');
    return null;
  }

  const btnOnClick = () => setIsEditModalOpen(true);
  const domain = domainMap[inputType];

  return (
    <>
      {mode === ViewModeDictionary.READONLY && valueObjects.length === 0 && (
        <ReadonlyWrapper>
          <TextOr />
        </ReadonlyWrapper>
      )}
      {mode !== ViewModeDictionary.READONLY && valueObjects.length === 0 && (
        <QButton data-cy={`add-${inputType}-button`} variant="link" onClick={btnOnClick}>
          {DisplayStrings.Add}
        </QButton>
      )}
      <QBox mt={2}>
        {isQrisDetailsLoading ? (
          <QBox w="100%" textAlign="center">
            <QSpinner />
          </QBox>
        ) : (
          parsedValueObjects.map((value: QLookupStandardValueObject) => {
            if (!qrisBulkDetails) {
              return null;
            }
            const qriDetails = qrisBulkDetails[value.qri];
            if (!qriDetails) {
              console.log(`QRI details for ${value.qri} not found`);
              return null;
            }

            if (referenceDrawerProps && domain !== 'users') {
              return (
                <ResourceLabelThatTriggersReferenceDrawer
                  key={`q-resource-${value.native_id}`}
                  resourceDomain={domain}
                  handleQResourceLabelClick={referenceDrawerProps.handleQResourceLabelClick}
                  qriDetails={qriDetails}
                  mode={mode}
                />
              );
            }

            return (
              <QResourceLabel
                key={`q-resource-${value.native_id}`}
                domain={domain}
                link={domain === 'users' ? undefined : qriDetails.identifiers.qri}
                openInNewTab={true}
              >
                <QText fontSize="sm">{formResolvedQriDisplayText(qriDetails)}</QText>
              </QResourceLabel>
            );
          })
        )}
      </QBox>
    </>
  );
};

export default QObjectField;
